import React from 'react'
import PropTypes from 'prop-types'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Box,
  Typography,
  Divider,
  Grid,
  Button,
  Slide
} from '@mui/material'
import { Close, WarningAmber, Cancel, Save, Edit } from '@mui/icons-material'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../../../../generic/systemValues/systemValueLabel'
import WorkerRequiredButton from '../../../common/WorkerRequiredButton'
import IncidenceDetailItem from './IncidenceDetailItem'
import IncidenceForm from './IncidenceForm'

/**
 * Transition for dialog entrance/exit.
 * @param {object} props
 * @param {import('react').Ref<any>} ref
 * @returns {JSX.Element}
 */
const Transition = React.forwardRef(function Transition (props, ref) {
  return <Slide direction='up' ref={ref} {...props} />
})

/**
 * Dialog to show incidence details and allow modifications/finishing.
 *
 * @param {object} props
 * @param {boolean} props.open Controls dialog visibility.
 * @param {Function} props.onClose Closes the dialog.
 * @param {object|null} props.viewModel Formatted data to display.
 * @param {boolean} props.isEditing Editing mode.
 * @param {Function} props.onStartModify Enters editing mode.
 * @param {Function} props.onCancelModify Cancels editing.
 * @param {Function} props.onSaveModify Saves modifications.
 * @param {Function} props.onFinish Finishes the incidence.
 * @param {boolean} props.finishSaving Saving state for finish.
 * @param {boolean} props.modifySaving Saving state for modify.
 * @param {any} props.modifyError Error for modify.
 * @param {object} props.formProps Props for incidence form.
 * @returns {JSX.Element|null}
 */
const IncidenceDetailDialog = ({
  open,
  onClose,
  viewModel,
  isEditing,
  onStartModify,
  onCancelModify,
  onSaveModify,
  onFinish,
  finishSaving,
  modifySaving,
  modifyError,
  formProps
}) => {
  const { t_ } = useSmartTranslation()

  if (!viewModel && !open) return null

  return (
    <Dialog
      fullScreen={false}
      maxWidth='xl'
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      sx={{
        '& .MuiDialog-paper': {
          width: '80%',
          height: '90%',
          maxHeight: '90%',
          borderRadius: 3,
          p: 1
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Stack direction='row' spacing={2} alignItems='center'>
          <WarningAmber color='error' sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant='h5' fontWeight='bold' color='error'>
              {isEditing ? t_('MODIFY INCIDENCE') : t_('INCIDENCE DETAILS')}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {viewModel?.startDate} - {viewModel?.startHour}
            </Typography>
          </Box>
        </Stack>
        <IconButton edge='end' color='inherit' onClick={onClose} aria-label='close'>
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3, bgcolor: 'background.default' }}>
        {isEditing
          ? (
            <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
              <IncidenceForm
                saving={modifySaving}
                error={modifyError}
                {...formProps}
              />
            </Box>
            )
          : (
            <Grid container spacing={3} sx={{ height: '100%', alignContent: 'flex-start' }}>
              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('Incidence Name')} value={viewModel?.incidenceName} />
              </Grid>
              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('Type / Status')} value={`${systemValueLabel(t_, 'status', viewModel?.statusValue) || viewModel?.status}`} />
              </Grid>
              <Grid item xs={12}>
                <IncidenceDetailItem title={t_('Description')} value={viewModel?.incidenceDescription} />
              </Grid>
              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('Created By')} value={viewModel?.createdBy} />
              </Grid>
              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('Date')} value={`${viewModel?.startDate} ${viewModel?.startHour}`} />
              </Grid>
              <Grid item xs={12}>
                <IncidenceDetailItem title={t_('Comments')} value={viewModel?.comment} />
              </Grid>
            </Grid>
            )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        {isEditing
          ? (
            <>
              <Button
                variant='outlined'
                color='inherit'
                startIcon={<Cancel />}
                onClick={onCancelModify}
                disabled={modifySaving}
              >
                {t_('CANCEL')}
              </Button>
              <WorkerRequiredButton
                variant='contained'
                color='primary'
                startIcon={<Save />}
                onClick={onSaveModify}
                disabled={modifySaving}
              >
                {modifySaving ? t_('SAVING...') : t_('SAVE CHANGES')}
              </WorkerRequiredButton>
            </>
            )
          : (
            <>
              <WorkerRequiredButton
                variant='outlined'
                startIcon={<Edit />}
                onClick={onStartModify}
              >
                {t_('MODIFY')}
              </WorkerRequiredButton>
              <WorkerRequiredButton
                variant='contained'
                color='error'
                onClick={onFinish}
                disabled={finishSaving}
                sx={{ px: 4 }}
              >
                {finishSaving ? t_('FINISHING...') : t_('FINISH INCIDENCE')}
              </WorkerRequiredButton>
            </>
            )}
      </DialogActions>
    </Dialog>
  )
}

IncidenceDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  viewModel: PropTypes.object,
  isEditing: PropTypes.bool,
  onStartModify: PropTypes.func,
  onCancelModify: PropTypes.func,
  onSaveModify: PropTypes.func,
  onFinish: PropTypes.func,
  finishSaving: PropTypes.bool,
  modifySaving: PropTypes.bool,
  modifyError: PropTypes.any,
  formProps: PropTypes.object
}

export default IncidenceDetailDialog
